import { Suspense, useEffect, useState } from 'react'
import { NavLink, Outlet, useMatches } from 'react-router-dom'
import {
  AudioWaveform,
  LayoutDashboard,
  Library,
  ListChecks,
  Loader2,
  Plug,
  TriangleAlert,
  Upload,
  Server,
  Wallet,
  Wifi,
  WifiOff,
  type LucideIcon,
} from 'lucide-react'

import { AuthBadge } from '@/components/LoginGate'
import { Separator } from '@/components/ui/separator'
import { ApiError, api } from '@/lib/api'
import { copyOnRightClick } from '@/lib/clipboard'
import { formatKrw } from '@/lib/format'
import { ROOM_ALL, useConnectionStatus, useRoom, useSocketEvent } from '@/lib/socket'
import { cn } from '@/lib/utils'

/**
 * 앱 껍데기 — 좌측 네비게이션 + 상단바.
 *
 * 🔴 네비게이션은 접히지 않습니다. 메뉴를 접어 두면 지금 어디에 있는지,
 *    무엇을 할 수 있는지가 화면에서 사라집니다 (펼쳐진 UX 기본).
 * 🔴 호버 시 배경색만 바뀝니다. 크기·여백이 변하면 목록이 흔들립니다.
 */

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
}

/**
 * 🔴 `채팅` 항목이 없는 것은 빠뜨린 것이 아닙니다.
 *
 *    전사문에 묻는 자리는 **작업 상세 페이지 안의 섹션**이 되었습니다
 *    (회장님 2026-08-11 지시). 근거가 되는 전사 원문과 확인용 플레이어가 그
 *    화면에 있기 때문입니다. 메뉴에 남겨 두면 "따로 있는 화면" 으로 읽혀
 *    그 결정과 어긋납니다.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: '대시보드', icon: LayoutDashboard, end: true },
  { to: '/upload', label: '업로드', icon: Upload },
  { to: '/jobs', label: '작업', icon: ListChecks },
  { to: '/library', label: '라이브러리', icon: Library },
  { to: '/providers', label: '프로바이더', icon: Plug },
  { to: '/costs', label: '비용', icon: Wallet },
  { to: '/fleet', label: '분산', icon: Server },
]

/** 라우트가 `handle` 로 알려 주는 화면 제목입니다 (src/routes.tsx). */
export interface RouteHandle {
  title?: string
  description?: string
}

function usePageMeta(): RouteHandle {
  const matches = useMatches()
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle as RouteHandle | undefined
    if (handle?.title) return handle
  }
  return {}
}

// ─────────────────────────────────────────────────────────────────────────────
// 상단바 조각
// ─────────────────────────────────────────────────────────────────────────────

function ConnectionChip() {
  const status = useConnectionStatus()

  if (status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-success/35 bg-success/10 px-2 py-1 text-xs text-success whitespace-nowrap shrink-0">
        <Wifi className="size-3.5" aria-hidden />
        실시간 연결됨
      </span>
    )
  }

  if (status === 'connecting') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-1 text-xs text-muted-foreground whitespace-nowrap shrink-0">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        연결하는 중입니다
      </span>
    )
  }

  // 🔴 끊긴 상태는 숨기지 않습니다. 이 상태에서는 진행률이 갱신되지 않습니다.
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-destructive/45 bg-destructive/12 px-2 py-1 text-xs text-destructive whitespace-nowrap shrink-0">
      <WifiOff className="size-3.5" aria-hidden />
      실시간 연결이 끊겼습니다
    </span>
  )
}

function TodayCostChip() {
  const [todayKrw, setTodayKrw] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useRoom(ROOM_ALL)

  useEffect(() => {
    const controller = new AbortController()
    api
      .costSummary(controller.signal)
      .then((summary) => {
        setTodayKrw(summary.today_krw)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === 'AbortError') return
        setError(cause instanceof ApiError ? cause.message : String(cause))
      })
    return () => controller.abort()
  }, [])

  // 비용이 발생하면 서버가 갱신된 오늘 합계를 함께 보내 줍니다.
  useSocketEvent('cost:recorded', (payload) => {
    setTodayKrw(payload.today_krw)
    setError(null)
  })

  if (error) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md border border-destructive/45 bg-destructive/12 px-2 py-1 text-xs text-destructive whitespace-nowrap shrink-0"
        title={error}
      >
        <TriangleAlert className="size-3.5" aria-hidden />
        오늘 사용액을 읽지 못했습니다
      </span>
    )
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-1 text-xs whitespace-nowrap shrink-0"
      {...copyOnRightClick(todayKrw === null ? '' : String(todayKrw), '오늘 사용액')}
    >
      <Wallet className="size-3.5 text-muted-foreground" aria-hidden />
      <span className="text-muted-foreground">오늘</span>
      <span className="num font-semibold">
        {todayKrw === null ? '…' : formatKrw(todayKrw)}
      </span>
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function PageFallback() {
  return (
    <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      화면을 불러오는 중입니다…
    </div>
  )
}

export default function AppShell() {
  const meta = usePageMeta()

  return (
    // 🔴 `min-h-0` 이 없으면 grid 항목의 기본 `min-height: auto` 때문에 내부
    //    `overflow-y-auto` 가 무력화되어 문서 전체가 대신 스크롤됩니다.
    //    그러면 비용 패널 같은 sticky 요소가 따라붙지 못합니다.
    // 🔴 `min-h-0` 이 없으면 grid 항목의 기본 `min-height: auto` 때문에 내부
    //    `overflow-y-auto` 가 무력화되어 문서 전체가 대신 스크롤됩니다.
    //
    // 🔴 모바일(< lg) 에서는 좌측 네비를 **하단 탭**으로 내립니다.
    //    412px 화면에서 224px 사이드바는 본문을 190px 로 눌러 카드 글자가
    //    서로 겹쳤습니다 (2026-08-10 Galaxy S25 Ultra 실측).
    //    "펼쳐진 UX" 원칙은 유지됩니다 — 메뉴는 접히는 것이 아니라 **자리를
    //    옮겨 여전히 전부 보입니다.**
    <div className="grid h-full min-h-0 grid-rows-[minmax(0,1fr)_auto] lg:grid-cols-[224px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)]">
      {/* ── 좌측 네비게이션 (lg 이상) ─────────────────────────────────── */}
      <nav
        className="hidden h-full flex-col gap-1 border-r border-border bg-[hsl(var(--inset))] p-3 lg:flex"
        aria-label="주 메뉴"
        data-component="side-nav"
      >
        <div className="flex flex-nowrap items-center gap-2 px-2 py-2">
          <AudioWaveform className="size-5 shrink-0 text-primary" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight whitespace-nowrap">voxctx</p>
            <p className="text-[11px] leading-tight text-muted-foreground whitespace-nowrap">
              음원 전사 컨텍스트
            </p>
          </div>
        </div>

        <Separator className="my-2" />

        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-nav={item.to}
              className={({ isActive }) =>
                cn(
                  // 호버·활성 모두 색만 바꿉니다. 두께·여백은 고정입니다.
                  'flex flex-nowrap items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors whitespace-nowrap',
                  isActive
                    ? 'bg-primary/15 font-medium text-primary'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )
              }
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              {item.label}
            </NavLink>
          )
        })}

        <div className="mt-auto px-1 pt-3">
          <ConnectionChip />
        </div>
      </nav>

      {/* ── 본문 ─────────────────────────────────────────────────────── */}
      <div className="flex h-full min-w-0 flex-col">
        {/*
          🔴 `overflow-x-auto` — 360px 실측(2026-08-11)에서 이 줄이 **문서 전체를
             가로로 21px 넓히고 있었습니다** (연결 상태·계정·오늘 사용액이 합쳐
             321px 인데 화면은 360px 이라 제목과 로고까지 함께 밀렸습니다).
             정보를 지우거나 숨기는 대신 **이 줄 안에서만** 가로로 흐르게 두어,
             페이지 본문이 통째로 밀리는 일을 막습니다.
        */}
        <header
          className="flex h-14 min-w-0 flex-nowrap items-center gap-3 overflow-x-auto border-b border-border px-4 lg:px-5"
          data-component="page-header"
        >
          {/* 모바일에서는 사이드바가 없으므로 로고를 헤더에 둡니다 */}
          <AudioWaveform className="size-5 shrink-0 text-primary lg:hidden" aria-hidden />
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold">{meta.title ?? 'voxctx'}</h1>
            {meta.description ? (
              <p className="hidden truncate text-xs text-muted-foreground sm:block">
                {meta.description}
              </p>
            ) : null}
          </div>
          <div className="ml-auto flex shrink-0 flex-nowrap items-center gap-2">
            {/* 사이드바가 없는 모바일에서도 연결 상태는 계속 보여야 합니다 */}
            <span className="lg:hidden">
              <ConnectionChip />
            </span>
            {/* 🔴 누구로 보고 있는지 늘 보입니다. 로컬 직접 접속이면 그 사실을 적습니다. */}
            <AuthBadge />
            <TodayCostChip />
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4 lg:p-5">
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* ── 하단 탭 네비게이션 (lg 미만) ──────────────────────────────── */}
      <nav
        className="flex flex-nowrap items-stretch gap-0.5 overflow-x-auto border-t border-border bg-[hsl(var(--inset))] px-1 py-1 lg:hidden"
        aria-label="주 메뉴"
        data-component="bottom-nav"
      >
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-nav={item.to}
              className={({ isActive }) =>
                cn(
                  'flex min-w-[58px] flex-1 shrink-0 flex-col items-center justify-center gap-0.5',
                  'rounded-md px-1.5 py-1.5 text-[11px] whitespace-nowrap transition-colors',
                  isActive
                    ? 'bg-primary/15 font-medium text-primary'
                    : 'text-muted-foreground',
                )
              }
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              {item.label}
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}
